import axiosClient from './axios-client'

export type CompanyCreationV2CapitalChoice = 'qonto' | 'banque'
type CompanyCreationV2QontoDepositMode = 'api' | 'partner_link'

export type SiegeType = 'madomiciliation' | 'domicile-dirigeant' | 'local-pro' | 'centre-tiers' | 'renonce'

// Tunnel public (Partie 1) ou démarrage depuis la page Formalités de l'espace client.
type CompanyCreationV2Origin = 'onboarding' | 'customer_space'

// Inscription domiciliation intégrée au tunnel création (groupe « Votre domiciliation ») — résumé
// de `src/utils/creation-domiciliation/state.js` exposé par `GET /session` ; le détail par pièce
// (statuts `VALIDATION_OK` / `VALIDATION_KO` / `SUSPENDED` / `VALIDATED` / `REOPENED`) est réservé
// à `GET /domiciliation` et `GET /justificatifs` (lot B).
type CompanyCreationV2DomiciliationSummary = {
  // Domiciliation payée à l'écran Siège et inscription pas terminée, ou parcours Netheos déjà créé.
  required: boolean
  // Parcours Netheos créé (au premier dépôt d'une pièce vérifiée sur l'écran Justificatifs).
  courseReady: boolean
  netheosStatus: string | null
  // Vérification manuelle en cours (`DOCUMENTS_A_VALIDER`) : page Justificatifs bloquée.
  blocked: boolean
  // Pièce d'identité et justificatif de domicile validés (auto ou opérateur).
  piecesValidated: boolean
  // Questionnaire (step 18) enregistré — dérivé de l'adresse de réexpédition.
  questionnaireDone: boolean
  // Contrat signé (`ATTENTE_ARCHIVAGE` / `ARCHIVE`).
  signatureDone: boolean
}

export type CompanyCreationV2Session = {
  step: number
  hasPassword: boolean
  // Email du compte : rattachement de la session LogRocket (`LogRocket.identify`).
  email: string
  // Choisis à la Partie 1 (`CompanyCreationOnboarding` puis copiés sur `CompanyCreation` par
  // `provision-account.js`) — nullable pour couvrir un dossier migré sans donnée fiable, cf. écran
  // Forme juridique (récap).
  juridicForm: string | null
  ownerIsPerson: boolean | null
  // Écran Siège — réhydratation complète de l'écran, librement rééditable ; seule l'adresse de
  // domiciliation d'un contrat existant est définitive, portée par le backend (cf. specs/
  // features/creation-societe-v2/paiements.md § 2).
  siegeType: SiegeType | null
  domiciliationAddressId: number | null
  siegeCurrentAddress: string | null
  siegeCenterName: string | null
  siegeCenterSiren: string | null
  siegeHostedByThirdParty: boolean | null
  siegeHostFirstname: string | null
  siegeHostLastname: string | null
  mainEstablishmentDifferent: boolean | null
  mainEstablishmentType: string | null
  mainEstablishmentAddress: string | null
  // Écran « Dépôt de capital » (step 13) — `null` tant qu'aucun choix n'est fait.
  postSubmitCapitalChoice: CompanyCreationV2CapitalChoice | null
  // `'api'` : le clic « Déposer avec Qonto » crée l'inscription via l'Onboarding API ;
  // `'partner_link'` (dirigeant personne physique non associé, refusé par l'API) : le clic ouvre le
  // lien partenaire Qonto et le serveur enregistre le choix comme « banque ».
  qontoDepositMode: CompanyCreationV2QontoDepositMode
  // Inscription Qonto déjà créée (clic « Déposer avec Qonto » passé par l'API) : l'écran Dépôt de
  // capital ne propose plus le choix, seulement « Continuer ».
  qontoAccountCreated: boolean
  // Dossier envoyé au formaliste (posé au choix du dépôt de capital) : pilote l'écran « Envoi du
  // dossier » (step 14) et l'état « done » du stepper.
  finalized: boolean
  // Pilote la sortie de l'écran « Envoi du dossier » : retour à l'espace client pour
  // `customer_space`, fin d'inscription domiciliation (si en attente) pour `onboarding`.
  origin: CompanyCreationV2Origin
  // Inscription domiciliation intégrée au tunnel (groupe « Votre domiciliation », steps 18 / 19) —
  // résumé de `src/utils/creation-domiciliation/state.js`. `null` toléré par prudence (réponse
  // d'un backend antérieur), le serveur le renvoie toujours.
  domiciliation: CompanyCreationV2DomiciliationSummary | null
}

export const fetchCompanyCreationV2Session = (token?: string) => axiosClient
  .get<CompanyCreationV2Session>('/api/company-creation-v2/session', { params: { token } })

// Démarrage d'un dossier depuis l'espace client (page Formalités) : crée le `CompanyCreation` que
// `GET /session` exige, pré-rempli depuis le compte, à l'étape Siège social (409 si un dossier
// existe déjà, 422 si la forme juridique du compte n'est pas prise en charge par ce tunnel).
export const startCompanyCreationV2 = () => axiosClient
  .post<{ step: number }>('/api/company-creation-v2/start')

export const setCompanyCreationV2Password = (password: string) => axiosClient
  .post<{ step: number }>('/api/company-creation-v2/password', { password })

// Écran purement récapitulatif (aucun champ, cf. VALIDATORS.formerecap de la maquette) — la seule
// action possible est d'avancer au step suivant.
export const saveCompanyCreationV2FormeRecap = () => axiosClient
  .post<{ step: number }>('/api/company-creation-v2/forme-recap')

type SetCompanyCreationV2FormeResponse = {
  juridicForm: string
  ownerIsPerson: boolean | null
}

// Changement de forme sur place (`App.setForme()` de la maquette) — recale côté serveur ce qui
// dépend de la forme (dirigeant, régime fiscal), sans effacer le reste des réponses déjà saisies.
export const setCompanyCreationV2Forme = (juridicForm: string) => axiosClient
  .post<SetCompanyCreationV2FormeResponse>('/api/company-creation-v2/forme-recap/forme', { juridicForm })

// Validation du reste de la page Siège au « Continuer » (type de siège hors domiciliation et ses
// champs, établissement principal). `madomiciliation` n'est accepté que si une adresse a déjà été
// réglée via `saveCompanyCreationV2Domiciliation` : l'adresse est alors relue depuis le contrat.
export type SaveCompanyCreationV2SiegePayload = {
  siegeType: SiegeType
  siegeCurrentAddress?: string
  siegeCenterSiren?: string
  siegeCenterName?: string
  // Cas "domicile-dirigeant" uniquement.
  siegeHostedByThirdParty?: boolean
  siegeHostFirstname?: string
  siegeHostLastname?: string
  // Établissement principal — posé dans tous les cas, indépendamment du type de siège choisi.
  mainEstablishmentDifferent?: boolean
  mainEstablishmentType?: string
  mainEstablishmentAddress?: string
}

type SiegeCommitment = '3ans' | '2ans' | '1an' | 'mensuel'

export const saveCompanyCreationV2Siege = (payload: SaveCompanyCreationV2SiegePayload) => (
  axiosClient.post<{ step: number }>('/api/company-creation-v2/siege', payload)
)

export type SaveCompanyCreationV2DomiciliationPayload = {
  domiciliationAddressId: number
  commitment: SiegeCommitment
}

type SaveCompanyCreationV2DomiciliationResponse = {
  // `false` : la banque exige une confirmation 3DS de la souscription neuve — à terminer côté
  // client (`stripe.confirmCardPayment(clientSecret)`), puis renvoyer exactement la même requête :
  // rien n'est écrit tant que le paiement n'est pas vérifié côté serveur.
  validated: boolean
  clientSecret?: string
}

// Choix + paiement de l'adresse de domiciliation depuis la modale d'engagement — seule action
// définitive de l'écran Siège, sans passage à l'étape suivante (on reste sur la page).
export const saveCompanyCreationV2Domiciliation = (
  payload: SaveCompanyCreationV2DomiciliationPayload,
) => (
  axiosClient.post<SaveCompanyCreationV2DomiciliationResponse>(
    '/api/company-creation-v2/siege/domiciliation',
    payload,
  )
)

// Devis d'un palier d'engagement, en centimes, calculé côté serveur depuis les Price Stripe réels
// (le front n'en calcule aucun). `dueCents` = premier paiement d'une souscription neuve (1 € le
// 1ᵉʳ mois en mensuel, période entière remisée sinon) ; la remise (coupon `once`) ne porte que sur
// ce premier paiement.
export type DomiciliationQuote = {
  commitment: SiegeCommitment
  months: number
  faceCents: number
  discountPercent: number
  dueCents: number
  dueTtcCents: number
  monthlyEquivalentCents: number
  savedCents: number
}

// Prix affichés sur les cartes d'adresse et la modale d'engagement — même produit Stripe pour
// toutes les adresses, indépendant de celle choisie (cf. specs/
// features/creation-societe-v2/paiements.md § 2).
export type DomiciliationPrice = {
  monthlyPriceCents: number
  quotes: DomiciliationQuote[]
  // Non nul si l'utilisateur a déjà un contrat de domiciliation avec adresse (venant de son compte
  // client) — dans ce cas, l'écran ne propose plus le catalogue d'adresses ni la modale
  // d'engagement, seulement la confirmation de cette adresse déjà souscrite.
  existingAddress: Address | null
  // Contrat souscrit à l'onboarding, sans adresse encore choisie : palier courant de l'abonnement
  // Stripe (source de vérité), et s'il peut encore être relevé ici — seulement pendant l'essai,
  // aucun paiement n'est déclenché par ce changement (prélèvement à `trialEnd`).
  existingCommitment: SiegeCommitment | null
  canChangeCommitment: boolean
  trialEnd: string | null
}

export const fetchCompanyCreationV2DomiciliationPrice = () => axiosClient
  .get<DomiciliationPrice>('/api/company-creation-v2/siege/domiciliation-price')

type CoDirigeantPayload = {
  type: 'physic' | 'moral'
  // Personne physique uniquement.
  gender?: 'MR' | 'MME'
  name?: string
  firstname?: string
  usageName?: string
  nationality?: string
  birthdate?: string
  birthCity?: string
  birthPostalCode?: string
  birthCountry?: string
  maritalStatus?: 'Célibataire' | 'Marié(e)' | 'Pacsé(e)' | 'Divorcé(e)' | 'Séparé(e)' | 'Veuf / Veuve'
  fatherName?: string
  motherName?: string
  fatherFirstname?: string
  motherFirstname?: string
  address?: string
  email?: string
  tel?: string
  // Participation à la société — requis pour tout co-dirigeant, physique ou moral.
  isAssociate?: boolean
  // Personne morale uniquement.
  socialDenomination?: string
  juridicForm?: string
  siren?: string
  registrationCountry?: string
  socialSiege?: string
  shareCapital?: string
  representativeName?: string
  representativeFirstname?: string
  representativeNationality?: string
  representativeBirthdate?: string
  representativeBirthCity?: string
  representativeBirthPostalCode?: string
  representativeBirthCountry?: string
  representativeEmail?: string
  representativeTel?: string
}

// Composants d'une adresse sélectionnée via `AddressField.onSelect` — `country` en ISO2, tel que
// renvoyé par le géocodage (`FormattedGeocodeAddress.country`).
export type CompanyCreationV2AddressParts = {
  street: string | null
  cp: string | null
  city: string | null
  country: string | null
}

type SaveCompanyCreationV2AddressPartsPayload = Partial<
Record<keyof CompanyCreationV2AddressParts, string>
>

export type CompanyCreationV2Dirigeant = {
  ownerIsPerson: boolean | null
  gender: string | null
  name: string | null
  firstname: string | null
  usageName: string | null
  // Liste dynamique côté UI, une seule chaîne séparée par des virgules en base (convention actée,
  // cf. specs/features/creation-societe-v2/parcours.md § 3).
  otherFirstnames: string | null
  // Formatée JJ/MM/AAAA par le serveur (`CompanyCreation.birthdate` est une vraie colonne DATE) —
  // cf. `Dirigeant/schema.ts` (`frDateToIso`) pour la conversion inverse à l'envoi.
  birthdate: string | null
  birthCity: string | null
  birthPostalCode: string | null
  birthCountry: string | null
  nationality: string | null
  maritalStatus: string | null
  fatherName: string | null
  motherName: string | null
  fatherFirstname: string | null
  motherFirstname: string | null
  ownerAddress: string | null
  // Composants de l'adresse personnelle (`AddressField.onSelect`), pour le représentant Netheos de
  // l'inscription domiciliation ; `null` tant qu'aucune adresse n'a été sélectionnée.
  ownerAddressParts: CompanyCreationV2AddressParts | null
  email: string | null
  tel: string | null
  representantIsOwner: boolean | null
  ownerScNumber: string | null
  hadPreviousNonSalariedActivity: boolean | null
  previousActivity: string | null
  previousActivityEndDate: string | null
  previousActivityCountry: string | null
  previousActivitySiret: string | null
  healthInsuranceOrganism: string | null
  isBiologistAffiliation: boolean | null
  isPharmacistAffiliation: boolean | null
  ownerCompanySocialDenomination: string | null
  ownerCompanyJuridicForm: string | null
  ownerCompanySiren: string | null
  ownerCompanyShareCapital: string | null
  // Représentant légal de la société dirigeante — bloc dédié (colonnes
  // `owner_company_representative_*`), sa propre logique de champs requis, indépendante du
  // dirigeant personne physique ci-dessus (pas de père/mère/n° de sécurité sociale ici).
  representativeGender: string | null
  representativeName: string | null
  representativeFirstname: string | null
  representativeOtherFirstnames: string | null
  representativeBirthdate: string | null
  representativeBirthCity: string | null
  representativeBirthPostalCode: string | null
  representativeBirthCountry: string | null
  representativeNationality: string | null
  representativeAddress: string | null
  representativeAddressParts: CompanyCreationV2AddressParts | null
  representativeEmail: string | null
  representativeTel: string | null
  representativeIsOwner: boolean | null
  coDirigeants: CoDirigeantPayload[]
}

export type SaveCompanyCreationV2DirigeantPayload = Partial<Omit<
CompanyCreationV2Dirigeant, 'coDirigeants' | 'ownerAddressParts' | 'representativeAddressParts'
>> & {
  coDirigeants?: CoDirigeantPayload[]
  ownerAddressParts?: SaveCompanyCreationV2AddressPartsPayload
  representativeAddressParts?: SaveCompanyCreationV2AddressPartsPayload
}

export const fetchCompanyCreationV2Dirigeant = () => axiosClient
  .get<CompanyCreationV2Dirigeant>('/api/company-creation-v2/dirigeant')

export const saveCompanyCreationV2Dirigeant = (payload: SaveCompanyCreationV2DirigeantPayload) => (
  axiosClient.post<{ step: number }>('/api/company-creation-v2/dirigeant', payload)
)

export type AssociePayload = {
  type: 'physic' | 'moral'
  // Personne physique uniquement.
  gender?: 'MR' | 'MME'
  name?: string
  firstname?: string
  usageName?: string
  otherFirstnames?: string
  nationality?: string
  birthdate?: string
  birthCity?: string
  birthPostalCode?: string
  birthCountry?: string
  maritalStatus?: 'Célibataire' | 'Marié(e)' | 'Pacsé(e)' | 'Divorcé(e)' | 'Séparé(e)' | 'Veuf / Veuve'
  fatherName?: string
  motherName?: string
  fatherFirstname?: string
  motherFirstname?: string
  address?: string
  email?: string
  tel?: string
  // Personne morale uniquement.
  socialDenomination?: string
  juridicForm?: string
  siren?: string
  registrationCountry?: string
  socialSiege?: string
  shareCapital?: string
  representativeName?: string
  representativeFirstname?: string
  representativeNationality?: string
  representativeBirthdate?: string
  representativeBirthCity?: string
  representativeBirthPostalCode?: string
  representativeBirthCountry?: string
  representativeEmail?: string
  representativeTel?: string
}

// Carte en lecture seule affichée en tête de l'écran Associés (dirigeant/co-dirigeants déjà
// associés, gérés sur l'écran Dirigeant — pas éditables ici).
type AssocieReadOnlyCard = {
  name: string
  label: string
}

export type CompanyCreationV2Associes = {
  juridicForm: string | null
  minAssocies: number
  associateCount: number
  readOnlyCards: AssocieReadOnlyCard[]
  associes: AssociePayload[]
}

export type SaveCompanyCreationV2AssociesPayload = {
  associes: AssociePayload[]
}

// La bascule bidirectionnelle de forme juridique (SASU⇄SAS, EURL⇄SARL, cf. `recomputeForme()` de
// la maquette) est décidée côté serveur (seul endroit qui connaît le nombre total d'associés,
// dirigeant/co-dirigeants inclus) — `formSwitch` non nul indique qu'une bascule vient d'avoir lieu,
// à afficher dans une modale (cf. `App.mini` de la maquette).
type SaveCompanyCreationV2AssociesResponse = {
  step: number
  juridicForm: string
  formSwitch: { from: string, to: string } | null
}

export const fetchCompanyCreationV2Associes = () => axiosClient
  .get<CompanyCreationV2Associes>('/api/company-creation-v2/associes')

export const saveCompanyCreationV2Associes = (payload: SaveCompanyCreationV2AssociesPayload) => (
  axiosClient.post<SaveCompanyCreationV2AssociesResponse>('/api/company-creation-v2/associes', payload)
)

export type CompanyCreationV2Societe = {
  socialDenomination: string | null
  commercialBrand: string | null
  sigle: string | null
  desiredDomainName: string | null
  ownStatuts: boolean | null
}

export type SaveCompanyCreationV2SocietePayload = Partial<CompanyCreationV2Societe>

export const fetchCompanyCreationV2Societe = () => axiosClient
  .get<CompanyCreationV2Societe>('/api/company-creation-v2/societe')

export const saveCompanyCreationV2Societe = (payload: SaveCompanyCreationV2SocietePayload) => (
  axiosClient.post<{ step: number }>('/api/company-creation-v2/societe', payload)
)

type ActivityPayload = {
  redaction?: string
  reglementee?: boolean
  justificatif?: string
  custom?: boolean
}

export type CompanyCreationV2Objet = {
  activities: ActivityPayload[]
  activityStartDate: string | null
  socialObject: string | null
}

export type SaveCompanyCreationV2ObjetPayload = {
  activities?: ActivityPayload[]
  activityStartDate?: string
  socialObject?: string
}

export const fetchCompanyCreationV2Objet = () => axiosClient
  .get<CompanyCreationV2Objet>('/api/company-creation-v2/objet')

export const saveCompanyCreationV2Objet = (payload: SaveCompanyCreationV2ObjetPayload) => (
  axiosClient.post<{ step: number }>('/api/company-creation-v2/objet', payload)
)

export type CompanyCreationV2Fiscalite = {
  juridicForm: string | null
  // Décomposés depuis `fiscal_year_closing_date` (pas de colonnes séparées), cf. `Fiscalite/
  // constants.ts`. Reconstruction au mieux si l'utilisateur revient sur l'écran — la donnée de
  // référence reste la date elle-même, pas ce couple.
  exerciceMois: string | null
  exerciceAnnee: 'N' | 'N+1' | null
  regimeMicroSocial: boolean | null
  regimeFiscalImpots: string | null
  regimeFiscalImpotsDetail: string | null
  regimeFiscalTva: string | null
  tvaSurOption: boolean | null
  tvaTrimestrielle: boolean | null
}

export type SaveCompanyCreationV2FiscalitePayload = Partial<Omit<CompanyCreationV2Fiscalite, 'juridicForm'>>

export const fetchCompanyCreationV2Fiscalite = () => axiosClient
  .get<CompanyCreationV2Fiscalite>('/api/company-creation-v2/fiscalite')

export const saveCompanyCreationV2Fiscalite = (payload: SaveCompanyCreationV2FiscalitePayload) => (
  axiosClient.post<{ step: number }>('/api/company-creation-v2/fiscalite', payload)
)

// Un apporteur au capital (dirigeant, co-dirigeant ou associé) — `id` correspond à `dirigeant` ou
// `shareholder-<id>`, cf. `capital.js` (backend). `locked` = non-associé, apport figé à 0.
// `shareCapitalOrigin` = origine des fonds (enum Qonto), demandée aux seules personnes physiques
// associées.
export type CapitalApporteur = {
  id: string
  type: 'physic' | 'moral'
  name: string
  badge: string
  locked: boolean
  amount: string
  shareCapitalOrigin: string[]
}

export type CompanyCreationV2Capital = {
  nominalShareValue: string | null
  capitalDepositary: 'neobanque' | 'banque' | null
  bankName: string | null
  titreLabel: 'actions' | 'parts sociales'
  apporteurs: CapitalApporteur[]
}

export type SaveCompanyCreationV2CapitalPayload = {
  nominalShareValue?: string
  capitalDepositary?: 'neobanque' | 'banque'
  bankName?: string
  apports?: Record<string, string>
  shareCapitalOrigins?: Record<string, string[]>
}

export const fetchCompanyCreationV2Capital = () => axiosClient
  .get<CompanyCreationV2Capital>('/api/company-creation-v2/capital')

export const saveCompanyCreationV2Capital = (payload: SaveCompanyCreationV2CapitalPayload) => (
  axiosClient.post<{ step: number }>('/api/company-creation-v2/capital', payload)
)

// Devis des frais de greffe et annonce légale, en centimes, résolu côté serveur depuis le price
// Stripe de la forme juridique (le front n'en calcule aucun) — cf. `frais.js`.
type LegalFeesQuote = {
  label: string
  ht: number
  vat: number
  ttc: number
  currency: string
}

export type CompanyCreationV2Frais = {
  // `invoice_id` renseigné sur le dossier, quelle qu'en soit l'origine (paiement anticipé à la
  // Partie 1, ancien tunnel, cet écran).
  paid: boolean
  // `null` si déjà payé, ou si aucune forme juridique n'est connue sur le dossier.
  quote: LegalFeesQuote | null
  // Carte enregistrée par défaut sur le customer Stripe — paiement en un clic si présente.
  savedCard: { brand: string, last4: string } | null
  siegeType: string | null
}

export const fetchCompanyCreationV2Frais = () => axiosClient
  .get<CompanyCreationV2Frais>('/api/company-creation-v2/frais')

export type PaymentTracking = {
  fbEventId: string
  fbp?: string | null
  fbc?: string | null
  liFatId?: string | null
  eventSourceUrl?: string | null
}

type PayCompanyCreationV2FraisResponse =
  | { status: 'paid', step: number }
  // La banque impose un 3DS sur la carte enregistrée : à terminer côté client
  // (`stripe.handleNextAction`), puis `completeCompanyCreationV2FraisPayment`.
  | { status: 'requires_action', clientSecret: string }
  // Aucune carte enregistrée (ou carte refusée au préalable) : formulaire carte Stripe Elements
  // sur ce même PaymentIntent.
  | { status: 'requires_payment_method', clientSecret: string }

export const payCompanyCreationV2Frais = (tracking: PaymentTracking) => axiosClient
  .post<PayCompanyCreationV2FraisResponse>('/api/company-creation-v2/frais/pay', { tracking })

export const completeCompanyCreationV2FraisPayment = (
  payload: { paymentIntentId: string, tracking: PaymentTracking },
) => axiosClient
  .post<{ status: 'paid', step: number }>('/api/company-creation-v2/frais/complete-payment', payload)

// « Continuer » quand les frais sont déjà réglés — refusé (409) sinon.
export const saveCompanyCreationV2Frais = () => axiosClient
  .post<{ step: number }>('/api/company-creation-v2/frais')

// Écran « Justificatifs » (Partie 3) — la liste des pièces est calculée côté serveur à partir des
// réponses du questionnaire (`justificatifs-rules.js`) ; le frontend ne fait que la rendre.
export type CompanyCreationV2JustificatifSection = 'identite' | 'siege' | 'activite' | 'statuts'

type CompanyCreationV2JustificatifFormat = 'pdf' | 'jpeg' | 'png'

// Pièces vérifiées par Netheos (dossier avec domiciliation : pièce d'identité et justificatif de
// domicile du dirigeant, lot B) — `rejected` porte le motif (`netheos` : refus automatique,
// `reopened` : le service client demande une autre pièce) et, juste après un dépôt, le nombre de
// tentatives restantes ; `awaiting_pair` : recto reçu, verso attendu avant soumission ;
// `not_submitted` : pièce complète jamais soumise (Netheos indisponible au dépôt), à redéposer.
export type CompanyCreationV2JustificatifVerification =
  | { status: 'none' }
  | { status: 'awaiting_pair' }
  | { status: 'not_submitted' }
  | { status: 'validated' }
  | { status: 'manual_review' }
  | { status: 'rejected', reason: 'netheos' | 'reopened', illegible?: boolean }

export type CompanyCreationV2JustificatifDocumentType = {
  value: string | null
  options: { value: string, label: string }[]
}

export type CompanyCreationV2JustificatifItem = {
  key: string
  section: CompanyCreationV2JustificatifSection
  label: string
  hint: string
  required: boolean
  status: 'missing' | 'received' | 'auto'
  formats: CompanyCreationV2JustificatifFormat[]
  // Renseignés seulement pour une pièce vérifiée par Netheos, `null` sinon (le type se choisit sur
  // la ligne recto : `documentType` est `null` sur la ligne verso).
  documentType: CompanyCreationV2JustificatifDocumentType | null
  verification: CompanyCreationV2JustificatifVerification | null
}

export type CompanyCreationV2Justificatifs = {
  step: number
  items: CompanyCreationV2JustificatifItem[]
  isComplete: boolean
  domiciliation: CompanyCreationV2DomiciliationSummary
  netheos: {
    // Vérification manuelle en cours : aucun dépôt possible, l'écran se rafraîchit seul.
    isSuspended: boolean
    // Faux tant que les pièces Netheos d'un dossier domicilié ne sont pas validées.
    canValidate: boolean
  }
  // Résultat de la soumission Netheos déclenchée par le dernier dépôt / choix de type ; sur un
  // `validation_failure`, contrôles en échec et codes d'erreur Netheos (diagnostic, non affichés).
  submission?: { result: string, keysWithError?: string[], errors?: number[] }
}

export const fetchCompanyCreationV2Justificatifs = () => axiosClient
  .get<CompanyCreationV2Justificatifs>('/api/company-creation-v2/justificatifs')

// Type d'une pièce vérifiée par Netheos, requis avant le dépôt du fichier (422
// `INVALID_DOCUMENT_TYPE`, 409 `NETHEOS_SUSPENDED` / `NETHEOS_PIECE_VALIDATED`) ; la réponse est la
// liste recalculée (verso facultatif pour un passeport).
export const setCompanyCreationV2JustificatifDocumentType = (key: string, documentType: string) => (
  axiosClient.put<CompanyCreationV2Justificatifs>(
    `/api/company-creation-v2/justificatifs/${encodeURIComponent(key)}/document-type`,
    { documentType },
  )
)

// Un fichier par requête (PDF, ou JPEG / PNG pour une pièce vérifiée par Netheos — `item.formats`,
// 20 Mo max ; 422 `DOCUMENT_TYPE_REQUIRED`, 409 `NETHEOS_SUSPENDED`, 502 `NETHEOS_UNAVAILABLE`).
// `axiosClient` force `application/json` : on laisse axios recalculer l'en-tête multipart (avec
// sa boundary) en le surchargeant explicitement.
export const uploadCompanyCreationV2Justificatif = (key: string, file: File) => {
  const formData = new FormData()
  formData.append('file', file)
  return axiosClient.post<CompanyCreationV2Justificatifs>(
    `/api/company-creation-v2/justificatifs/${encodeURIComponent(key)}`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  )
}

type ValidateCompanyCreationV2JustificatifsResponse = {
  step: number
}

// Validation bloquante (422 `JUSTIFICATIFS_MISSING` avec les libellés manquants, 422
// `NETHEOS_PIECES_PENDING` / `NETHEOS_SUSPENDED` pour un dossier domicilié) qui avance le step à
// « Dépôt de capital » ; l'envoi réel du dossier au formaliste se joue au choix du dépôt.
export const validateCompanyCreationV2Justificatifs = () => axiosClient
  .post<ValidateCompanyCreationV2JustificatifsResponse>('/api/company-creation-v2/justificatifs/validate')

// Projet de statuts (PDF) de l'écran « Envoi du dossier » — servi seulement une fois le dossier
// envoyé et si le client ne fournit pas ses propres statuts (409 sinon, 422 si la forme juridique
// n'a pas de template).
export const downloadCompanyCreationV2Statuts = () => axiosClient
  .get<Blob>('/api/company-creation-v2/recap/statuts', { responseType: 'blob' })

type CompanyCreationV2StatutsDownloadUrl = {
  // URL S3 pré-signée (téléchargement), valable 24 h : à demander au clic.
  url: string
  filename: string
  // Vrai quand ce sont les statuts signés électroniquement (Firma), sinon le fichier courant.
  signed: boolean
}

// Statuts du dossier envoyé, pour l'espace client (onglet Formalités) : signés en priorité, sinon
// le fichier courant (projet généré ou statuts déposés par le client, éventuellement re-déposés
// par le formaliste). 409 si le dossier n'est pas finalisé, 404 `STATUTS_NOT_AVAILABLE` sans
// fichier.
export const getCompanyCreationV2StatutsDownloadUrl = () => axiosClient
  .get<CompanyCreationV2StatutsDownloadUrl>('/api/company-creation-v2/statuts/download-url')

type CompanyCreationV2CapitalDepositCertificateResponse = {
  received: boolean
}

// Attestation de dépôt de capital déposée depuis l'espace client (onglet Formalités), pour un
// dossier déjà envoyé au formaliste (409 `DOSSIER_NOT_FINALIZED` sinon). PDF uniquement, 20 Mo max
// (400 `Invalid PDF files` / message sinon) ; un nouveau dépôt remplace le précédent. Même
// en-tête multipart explicite que `uploadCompanyCreationV2Justificatif`.
export const uploadCompanyCreationV2CapitalDepositCertificate = (file: File) => {
  const formData = new FormData()
  formData.append('file', file)
  return axiosClient.post<CompanyCreationV2CapitalDepositCertificateResponse>(
    '/api/company-creation-v2/capital-deposit-certificate',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  )
}

type CompanyCreationV2DepotResponse = {
  step: number
  // Choix enregistré : `banque` même pour un clic Qonto quand `qontoDepositMode` vaut
  // `partner_link`
  choice: CompanyCreationV2CapitalChoice
  // URL du tunnel d'inscription Qonto (`registration.redirection_link`), `null` si choix banque
  qontoRedirectionLink: string | null
}

// Prérequis : justificatifs complets (422 `JUSTIFICATIFS_MISSING` sinon). Choix Qonto : le serveur
// crée l'inscription via l'API Qonto (avec statuts et pièces d'identité) avant de répondre (422 si
// la forme juridique n'est pas encore prise en charge, 502 si Qonto échoue — `error` porte le
// message), sauf en mode lien partenaire (`session.qontoDepositMode`) : « banque » enregistré sans
// appel. Dans tous les cas le dossier est envoyé au formaliste (`session.finalized`).
export const saveCompanyCreationV2Depot = (choice: CompanyCreationV2CapitalChoice) => axiosClient
  .post<CompanyCreationV2DepotResponse>('/api/company-creation-v2/depot', { choice })

// --- Inscription domiciliation : Questionnaire (step 18, lot C) ---------------------------------

// Adresse de réexpédition du courrier (`User.expedition_*`) — `null` tant que le questionnaire
// n'a pas été enregistré (rien n'y est écrit avant lui dans ce tunnel). `formattedAddress` est la
// chaîne à réafficher dans `AddressField`, reconstruite côté serveur depuis les composants ;
// `country` est rendu en ISO2 (comme `AddressField.onSelect`), le serveur stockant un libellé.
type CompanyCreationV2DomiciliationExpedition = {
  name: string | null
  premise: string | null
  address: string | null
  cp: string | null
  city: string | null
  country: string | null
  formattedAddress: string | null
}

// Questionnaire LCB-FT + réexpédition (`GET /domiciliation/questionnaire`) : les seules
// informations restantes pour le contrat de domiciliation, tout ce qui est déjà connu (dirigeant,
// activité, marque) étant repris du dossier de création côté serveur.
export type CompanyCreationV2DomiciliationQuestionnaire = {
  expedition: CompanyCreationV2DomiciliationExpedition
  hasExpert: boolean | null
  expertName: string | null
  expertAddress: string | null
  ppe: boolean | null
  // Origines des fonds cochées, « Autre » inclus quand une précision est enregistrée.
  ppeOrigin: string[]
  ppeOriginOther: string | null
  hasActivityLocation: boolean | null
  activityLocation: string | null
  // Signataire (dirigeant, ou représentant légal de la société dirigeante) pour l'attestation
  // d'origine des fonds.
  representantFullName: string | null
}

export type SaveCompanyCreationV2DomiciliationQuestionnairePayload = {
  expedition: {
    name: string
    premise?: string
    // Composants de l'adresse sélectionnée (`AddressField.onSelect`, `country` ISO2).
    address: string
    cp: string
    city: string
    country: string
  }
  hasExpert: boolean
  expertName?: string
  expertAddress?: string
  ppe: boolean
  ppeOrigin?: string[]
  ppeOriginOther?: string
  hasActivityLocation: boolean
  activityLocation?: string
}

type SaveCompanyCreationV2DomiciliationQuestionnaireResponse = {
  step: number
  domiciliation: CompanyCreationV2DomiciliationSummary
}

export const fetchCompanyCreationV2DomiciliationQuestionnaire = () => axiosClient
  .get<CompanyCreationV2DomiciliationQuestionnaire>('/api/company-creation-v2/domiciliation/questionnaire')

// Formulaire bloquant, validé côté client puis serveur (400 sur un corps incohérent).
// Préconditions serveur : parcours Netheos créé (409 `COURSE_NOT_READY`), dossier envoyé (409
// `DOSSIER_NOT_SENT`), contrat non signé (409 `ALREADY_SIGNED`). Écrit la réexpédition et le
// questionnaire sur le compte, régénère le contrat chez Netheos (502 `NETHEOS_UNAVAILABLE` :
// réponses conservées, requête rejouable) puis avance `step` vers la Signature. Rejouable tant que
// le contrat n'est pas signé.
export const saveCompanyCreationV2DomiciliationQuestionnaire = (
  payload: SaveCompanyCreationV2DomiciliationQuestionnairePayload,
) => axiosClient.post<SaveCompanyCreationV2DomiciliationQuestionnaireResponse>(
  '/api/company-creation-v2/domiciliation/questionnaire',
  payload,
)

// --- Groupe « Votre domiciliation » : Signature du contrat (step 19, lot D) ---------------------
// Cf. specs/features/creation-societe-v2/integrations.md § 1.

export type CompanyCreationV2DomiciliationSignature = {
  // Token d'accès du participant pour le SignBook Netheos ; `null` une fois le contrat signé.
  token: string | null
  signatureDone: boolean
}

// Préconditions côté serveur (409, `error` : `COURSE_NOT_READY`, `NETHEOS_SUSPENDED`,
// `NETHEOS_PIECES_PENDING`, `QUESTIONNAIRE_REQUIRED`, avec le résumé `domiciliation`) ; 502
// `NETHEOS_UNAVAILABLE` si Netheos ne répond pas.
export const fetchCompanyCreationV2DomiciliationSignature = () => axiosClient
  .get<CompanyCreationV2DomiciliationSignature>('/api/company-creation-v2/domiciliation/signature')

type FinalizeCompanyCreationV2DomiciliationResponse = {
  domiciliation: CompanyCreationV2DomiciliationSummary
}

// À appeler dès que le SignBook signale la signature : acceptation du dossier Netheos, statut
// `ATTENTE_ARCHIVAGE`, fin de la domiciliation en attente. Idempotent (rejouable après un échec) ;
// 409 `SIGNATURE_NOT_COMPLETED` si le dossier Netheos n'est pas terminé, 502 `NETHEOS_UNAVAILABLE`.
export const finalizeCompanyCreationV2Domiciliation = () => axiosClient
  .post<FinalizeCompanyCreationV2DomiciliationResponse>('/api/company-creation-v2/domiciliation/signature/finalize')
